import { captureException } from "@sentry/browser";
import * as t from "io-ts";
import { t as ttag } from "ttag";

import { RetailStore } from "../models/location";
import {
    Latitude,
    Longitude,
    ProductUUID,
    SyncStoreID,
} from "../models/nominals";
import { check } from "../models/utils";
import { trackEvent } from "../utils/analytics";
import { getPageSetting } from "../utils/settings";

type ITextQuery = {
    // Search location by string
    q: string;
};

type IGeoQuery = {
    // Search location by latitude and longitude
    origLat: Latitude;
    origLng: Longitude;
};

type IOtherFilters = {
    // Max distance from search location
    distance?: number;

    // Return store distance to center
    show_distance?: "true" | "false";

    // Filter by store ID
    external_id?: SyncStoreID;

    // Filter by store branch
    brand?: string;

    // Filter by store category
    category?: string | null;

    // Filter by retailer name
    retailer?: string[];

    // Filter by product availability
    product?: ProductUUID;

    // Max results. Default sot 500.
    limit?: number;

    // Limit by country.
    country?: string;

    // Slot filters
    has_slot_stearns_foster?: string;
    has_slot_tempur?: string;
    has_slot_sealy?: string;
};

export type IQueryParams = (ITextQuery | IGeoQuery) & IOtherFilters;

export enum StoreCategory {
    // TODO(i18n): These user-facing store category labels need to be translatable. Consider moving to a function that returns translated strings: FLAGSHIP: t`Tempur-Pedic Flagship Store`, ELITE: t`Elite`, NOT_ELITE: t`Not Elite`
    FLAGSHIP = "Tempur-Pedic Flagship Store",
    ELITE = "Elite",
}

const buildQueryParams = (query: IQueryParams): URLSearchParams => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
            for (const item of value) {
                params.append(key, String(item));
            }
        } else {
            params.append(key, String(value));
        }
    }
    return params;
};

export const searchStores = async (
    query: IQueryParams,
): Promise<ReadonlyArray<t.TypeOf<typeof RetailStore>>> => {
    const apiURL = getPageSetting("retail_locator_api");
    const brand = getPageSetting("retail_locator_brand");
    const country = getPageSetting("retail_locator_country");
    query = {
        brand: brand ?? undefined,
        show_distance: "true",
        distance: 3000,
        country: country ?? undefined,
        ...query,
    };
    const params = buildQueryParams(query);

    try {
        const resp = await fetch(`${apiURL}/api/locations/?${params}`, {
            headers: { Accept: "application/json" },
            credentials: "omit", // External API - don't send cookies
        });
        if (!resp.ok) {
            throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
        }
        const stores = await resp.json();
        // Log stores result
        if (stores.length === 0) {
            trackEvent(
                "RetailLocator",
                "storesNotFound",
                "Stores Not Found at Distance",
                {
                    nonInteraction: true,
                    distance: query.distance,
                },
            );
        } else if (stores.length >= 500) {
            trackEvent(
                "RetailLocator",
                "tooManyStoresFound",
                "Too Many Stores at Distance",
                {
                    nonInteraction: true,
                    distance: query.distance,
                },
            );
        }
        trackEvent(
            "RetailLocator",
            "numStoresFound",
            ttag`Distance: ${query.distance}mi`,
            {
                nonInteraction: true,
                numStores: stores.length,
            },
        );
        // Decode types
        return check(t.array(RetailStore).decode(stores));
    } catch (err) {
        console.log(err);
        captureException(err);
        return [] as ReadonlyArray<t.TypeOf<typeof RetailStore>>;
    }
};
